/**
 * Pure sort/filter engine for the Clicks grid: the Excel-style column filters
 * (#8/#12) and the lock-aware multi-sort (#14 — "lock a column as a sort key").
 *
 * No React, no tanstack, so it unit-tests on its own and is reused by the
 * table, the chart, and a future CSV export. The table keeps SortRule[] as the
 * source of truth (it carries the `locked` flag tanstack's SortingState has no
 * room for) and hands tanstack the derived {id,desc}[]; the column filters go
 * in as custom filterFns.
 */

import { z } from "zod";
import { ATTRIBUTE_COLUMNS, DOMAIN_ONLY_ATTRIBUTES } from "@/config/metrics";

export type SortRule = { id: string; desc: boolean; locked: boolean };

/**
 * Locked columns always sort before unlocked ones; within each group the array
 * order is the priority. So "lock Revenue" keeps it the primary key while you
 * click Clicks to sort inside each revenue tier.
 */
export function orderedRules(rules: SortRule[]): SortRule[] {
  return [...rules.filter((r) => r.locked), ...rules.filter((r) => !r.locked)];
}

/** {id,desc}[] for tanstack's SortingState, in effective priority order. */
export function toSortingState(
  rules: SortRule[],
): { id: string; desc: boolean }[] {
  return orderedRules(rules).map(({ id, desc }) => ({ id, desc }));
}

/**
 * A header click. Plain click = sort by this one column, cycling
 * asc → desc → off, clearing other UNLOCKED sorts (locked ones stay).
 * additive (shift-click) = add/toggle this column as an extra sort key.
 * A locked column just flips direction and stays locked + leading.
 */
export function applyHeaderClick(
  rules: SortRule[],
  id: string,
  additive: boolean,
): SortRule[] {
  const existing = rules.find((r) => r.id === id);

  if (existing?.locked) {
    return rules.map((r) => (r.id === id ? { ...r, desc: !r.desc } : r));
  }

  if (additive) {
    if (!existing) return [...rules, { id, desc: false, locked: false }];
    if (!existing.desc)
      return rules.map((r) => (r.id === id ? { ...r, desc: true } : r));
    return rules.filter((r) => r.id !== id);
  }

  // Plain click: keep locked rules, replace the unlocked set with just this one.
  const locked = rules.filter((r) => r.locked);
  if (!existing) return [...locked, { id, desc: false, locked: false }];
  if (!existing.desc) return [...locked, { id, desc: true, locked: false }];
  return locked;
}

/** The padlock toggle. Locking a not-yet-sorted column adds it (asc, locked). */
export function toggleLock(rules: SortRule[], id: string): SortRule[] {
  const existing = rules.find((r) => r.id === id);
  if (existing) {
    return rules.map((r) => (r.id === id ? { ...r, locked: !r.locked } : r));
  }
  return [...rules, { id, desc: false, locked: true }];
}

const TEXT_ORDER = new Intl.Collator("en", { sensitivity: "base", numeric: true });

/** Comparator over the rules, for sorting outside tanstack (export, chart). */
export function compareByRules<T>(
  rules: SortRule[],
  getValue: (row: T, id: string) => number | string | null | undefined,
): (a: T, b: T) => number {
  const ordered = orderedRules(rules);
  return (a, b) => {
    for (const r of ordered) {
      const av = getValue(a, r.id);
      const bv = getValue(b, r.id);
      // A null cell sorts last in both directions — an empty value is not "small".
      if (av == null && bv == null) continue;
      if (av == null) return 1;
      if (bv == null) return -1;
      // Text: case-insensitive, digits as numbers ("bet365" < "NV Casino", "2" < "10").
      const cmp =
        typeof av === "string" && typeof bv === "string"
          ? TEXT_ORDER.compare(av, bv)
          : av < bv ? -1 : av > bv ? 1 : 0;
      if (cmp !== 0) return r.desc ? -cmp : cmp;
    }
    return 0;
  };
}

// ─── Excel-style column filters (#8 / #12) ───

export type TextOp =
  | "contains"
  | "notContains"
  | "equals"
  | "notEquals"
  | "startsWith"
  | "endsWith"
  | "isEmpty"
  | "isNotEmpty";

export type NumberOp =
  | "eq"
  | "ne"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "inList";

export type ColumnFilter =
  | { kind: "text"; op: TextOp; value: string }
  | { kind: "number"; op: Exclude<NumberOp, "between" | "inList">; value: number }
  | { kind: "number"; op: "between"; min: number; max: number }
  | { kind: "number"; op: "inList"; values: number[] };

export function matchesText(op: TextOp, query: string, cell: string): boolean {
  const c = cell.toLowerCase();
  const q = query.trim().toLowerCase();
  switch (op) {
    case "contains":
      return c.includes(q);
    case "notContains":
      return !c.includes(q);
    case "equals":
      return c === q;
    case "notEquals":
      return c !== q;
    case "startsWith":
      return c.startsWith(q);
    case "endsWith":
      return c.endsWith(q);
    case "isEmpty":
      return cell.trim() === "";
    case "isNotEmpty":
      return cell.trim() !== "";
  }
}

/**
 * "= 0.35" against a stored 0.346123: equal at the precision typed, but never
 * coarser than cents - "= 250" matches $250.004, not $250.40. The screen
 * rounds rates to 4 places, so an exact compare missed what the user sees.
 */
function sameNumber(cell: number, typed: number): boolean {
  const frac = String(typed).split(".")[1];
  const places = Math.max(2, String(typed).includes("e") ? 0 : (frac?.length ?? 0));
  return cell.toFixed(places) === typed.toFixed(places);
}

export function matchesNumber(
  filter: Extract<ColumnFilter, { kind: "number" }>,
  cell: number,
): boolean {
  switch (filter.op) {
    case "eq":
      return sameNumber(cell, filter.value);
    case "ne":
      return !sameNumber(cell, filter.value);
    case "gt":
      return cell > filter.value;
    case "gte":
      return cell >= filter.value;
    case "lt":
      return cell < filter.value;
    case "lte":
      return cell <= filter.value;
    case "between":
      // Either order: "Between 100 and 10" means 10..100.
      return cell >= Math.min(filter.min, filter.max) && cell <= Math.max(filter.min, filter.max);
    case "inList":
      return filter.values.some((v) => sameNumber(cell, v));
  }
}

/** The row fields the grid reads to resolve a column. */
export type GridRowShape = {
  dimension_value: string;
  dimension_parts?: string[] | null;
  dimension_brand?: string | null;
  dimension_campaign_group?: string | null;
  total_revenue?: number | null;
  unique_global?: number | null;
};

/**
 * uEPC is not a server field: revenue per unique visitor, derived in the
 * browser (see getMetricNumericValue). Before 01.10.2026 the grid read
 * row.uepc, got nothing, and its sort did nothing and its filter hid every row.
 */
export function uepc(row: { total_revenue?: number | null; unique_global?: number | null }): number {
  const visitors = row.unique_global ?? 0;
  return visitors > 0 ? (row.total_revenue ?? 0) / visitors : 0;
}

const num = (v: unknown) => (typeof v === "number" ? v : 0);
const ratio = (a: unknown, b: unknown) => (num(b) > 0 ? num(a) / num(b) : 0);

/**
 * Metrics derived in the browser from fields every row - and the compared
 * period's row - already has, the way Keitaro Toolkit shows them. Display,
 * sort, column filters, compare and CSV all read them from here.
 */
export const DERIVED_METRICS: Record<string, (row: object) => number> = {
  uepc: (r) => uepc(r as { total_revenue?: number | null; unique_global?: number | null }),
  uepc_campaign: (r) => ratio((r as Record<string, unknown>).total_revenue, (r as Record<string, unknown>).unique_campaign),
  approve_percent: (r) => 100 * ratio((r as Record<string, unknown>).total_sales, (r as Record<string, unknown>).total_conversions),
  revenue_per_sale: (r) => ratio((r as Record<string, unknown>).total_revenue, (r as Record<string, unknown>).total_sales),
  revenue_per_conversion: (r) => ratio((r as Record<string, unknown>).total_revenue, (r as Record<string, unknown>).total_conversions),
  unique_percent: (r) => 100 * ratio((r as Record<string, unknown>).unique_global, (r as Record<string, unknown>).total_clicks),
  unique_campaign_percent: (r) => 100 * ratio((r as Record<string, unknown>).unique_campaign, (r as Record<string, unknown>).total_clicks),
  unique_stream_percent: (r) => 100 * ratio((r as Record<string, unknown>).unique_stream, (r as Record<string, unknown>).total_clicks),
};

/** Text columns that are not metrics: the grouping column and row attributes. */
// Attribute text, dates (ISO, so text order is date order) and yes/no; amounts and counts are numbers.
export const TEXT_COLUMNS = new Set([
  "dimension",
  "brand",
  "campaign_group",
  ...[...ATTRIBUTE_COLUMNS.values()].filter((c) => c.kind !== "usd" && c.kind !== "int").map((c) => c.id),
]);

/**
 * What a column holds for one row, given the grouping `dims`.
 *
 * Brand and Campaign Group come from the grouping itself when they are part
 * of it: grouped by Brand, the server leaves dimension_brand empty (the first
 * column already says it), and a "Brand contains X" filter read that empty
 * value and hid every row (30.09.2026).
 */
export function columnValue(
  row: GridRowShape,
  id: string,
  dims: string[],
): string | number | null {
  if (id === "dimension") return row.dimension_value;
  if (id === "brand" || id === "campaign_group") {
    const attr = id === "brand" ? row.dimension_brand : row.dimension_campaign_group;
    if (attr != null) return attr;
    const k = dims.indexOf(id);
    if (k < 0) return null;
    return row.dimension_parts?.[k] ?? (dims.length === 1 ? row.dimension_value : null);
  }
  if (id in DERIVED_METRICS) return DERIVED_METRICS[id](row);
  const v = (row as Record<string, unknown>)[id];
  // Attributes merged onto the row: text and dates are strings, amounts numbers, yes/no a word.
  if (ATTRIBUTE_COLUMNS.has(id)) return typeof v === "string" || typeof v === "number" ? v : typeof v === "boolean" ? (v ? "Yes" : "No") : null;
  return typeof v === "number" ? v : null;
}

/**
 * Capture rate, %: GSC clicks scaled to 30 days over the row's monthly keyword
 * volume - the share of the brand's searches this domain takes. Summed over a
 * brand's domains it is the brand's capture rate. null when either side is
 * missing or GSC covered no day of the period.
 */
export function captureRate(
  gscClicks: number | null | undefined,
  kvLast: number | null | undefined,
  gscDays: number,
): number | null {
  if (gscClicks == null || kvLast == null || kvLast <= 0 || gscDays <= 0) return null;
  return ((gscClicks * 30) / gscDays / kvLast) * 100;
}

/**
 * Days of the period GSC can have data for: the end is clipped at GSC's latest
 * day (it lags ~3 days), so a "last 7 days" range is not divided by 7. 0 for an
 * open-ended ("all time") range.
 */
export function gscDaysInRange(start: string | null, end: string, gscLatest: string | null | undefined): number {
  if (!start) return 0;
  const last = gscLatest && gscLatest < end ? gscLatest : end;
  return Math.max(0, Math.round((Date.parse(last) - Date.parse(start)) / 86_400_000) + 1);
}

export type ColKind = "text" | "number";

/** What the filter popover edits per column; `a`/`b` are the raw inputs. */
export type FilterDraft = { op: TextOp | NumberOp; a: string; b: string };

/** "$1 250", "12%", "1250" -> 1250; anything else NaN. */
const parseNumber = (raw: string): number => {
  const t = raw.replace(/[\s$%_]/g, "");
  return t === "" ? NaN : Number(t);
};

/**
 * The draft as a filter, or null while it is incomplete (Apply then clears
 * the column). Before 01.10.2026 an empty or trailing-comma "In list" turned
 * "" into 0 and kept only zero rows.
 */
export function draftToFilter(kind: ColKind, draft: FilterDraft | undefined): ColumnFilter | null {
  if (!draft) return null;
  if (kind === "text") {
    const op = draft.op as TextOp;
    if (op === "isEmpty" || op === "isNotEmpty") return { kind: "text", op, value: "" };
    return draft.a.trim() ? { kind: "text", op, value: draft.a } : null;
  }
  const op = draft.op as NumberOp;
  if (op === "between") {
    const min = parseNumber(draft.a);
    const max = parseNumber(draft.b);
    return Number.isNaN(min) || Number.isNaN(max) ? null : { kind: "number", op, min, max };
  }
  if (op === "inList") {
    const values = draft.a.split(/[\s,;|]+/).map(parseNumber).filter((n) => !Number.isNaN(n));
    return values.length ? { kind: "number", op, values } : null;
  }
  const value = parseNumber(draft.a);
  return Number.isNaN(value) ? null : { kind: "number", op, value };
}

/** Rows passing every committed column filter (AND). */
export function filterRows<T>(
  rows: T[],
  filters: Record<string, ColumnFilter>,
  value: (row: T, id: string) => string | number | null,
): T[] {
  const active = Object.entries(filters);
  if (!active.length) return rows;
  return rows.filter((row) =>
    active.every(([id, f]) => {
      const raw = value(row, id);
      if (f.kind === "text") return matchesText(f.op, f.value, raw == null ? "" : String(raw));
      return raw != null && matchesNumber(f, Number(raw));
    }),
  );
}

/**
 * Which column filters/sorts survive a change of grouping. The first column
 * ("dimension") means something else under a new grouping, so it goes; a
 * column that is no longer on screen goes too, instead of filtering rows out
 * invisibly. Brand/Campaign Group stay when they are part of the grouping.
 */
export function keepAfterRegroup(
  id: string,
  ctx: { prevDims: string[]; dims: string[]; columns: Set<string> },
): boolean {
  if (id === "dimension") return ctx.prevDims.join(",") === ctx.dims.join(",");
  // A Brand condition stays for rows that have a brand: grouped by Brand, Domain or URL.
  if (id === "brand") return ctx.dims.some((d) => BRANDED_DIMS.has(d));
  if (id === "campaign_group") return true; // filled for every grouping but itself, where it is the dim
  if (id.startsWith("kv_") || id.startsWith("gsc_") || id === "capture_rate" || ATTRIBUTE_COLUMNS.has(id))
    return appliesToGrouping(id, ctx.dims) && ctx.columns.has(id);
  return ctx.columns.has(id);
}

const BRANDED_DIMS = new Set(["brand", "domain", "url"]);

/**
 * Whether a column has values under this grouping - what the grid can draw,
 * so the Columns picker greys the rest (variant 4E). Mirrors get_dynamic_stats
 * (20261027193330): brand comes with a domain or URL in the grouping
 * (brand_col), Campaign Group with anything but itself (cg_col) - both "any
 * position", for two-level groupings too. Keyword volume hangs off the brand,
 * GSC joins onto plain domain rows only (Clicks.tsx mergedData); Domain
 * Tracker fields onto a row with a domain or URL in it, like brand. A display
 * rule: keepAfterRegroup keeps its own Brand / Campaign Group cases.
 */
export function appliesToGrouping(id: string, dims: string[]): boolean {
  // Indexation / DMCA is about the domain itself: not on URL rows (they would read as the URL's).
  if (DOMAIN_ONLY_ATTRIBUTES.has(id)) return dims.includes("domain");
  if (id === "brand" || ATTRIBUTE_COLUMNS.has(id)) return dims.some((d) => d === "domain" || d === "url");
  if (id === "campaign_group") return !dims.includes("campaign_group");
  if (id.startsWith("kv_")) return dims.some((d) => BRANDED_DIMS.has(d));
  if (id.startsWith("gsc_") || id === "capture_rate") return dims.join(",") === "domain";
  return true;
}

// ─── row selection (the checkboxes) ───

export type SelectAllState = "none" | "some" | "all";

/** The header checkbox: are all / some / none of the shown rows selected? */
export function selectAllState(shown: string[], selected: ReadonlySet<string>): SelectAllState {
  let n = 0;
  for (const v of shown) if (selected.has(v)) n++;
  if (n === 0) return "none";
  return n === shown.length ? "all" : "some";
}

/**
 * A click on the header checkbox. All shown rows selected: unselect them (rows
 * selected but filtered out stay selected). Otherwise select every shown row,
 * shown rows first in their on-screen order - the chart takes the first five,
 * so "select all" overlays the top five of the current sort. Each value once,
 * even if two shown rows share it.
 */
export function toggleSelectAll(shown: string[], selected: string[]): string[] {
  if (selectAllState(shown, new Set(selected)) === "all") {
    const drop = new Set(shown);
    return selected.filter((v) => !drop.has(v));
  }
  return [...new Set([...shown, ...selected])];
}

/**
 * The selected rows for Export selected / Copy list: every loaded row whose
 * value is selected, filtered out or not (the selection is an explicit
 * choice, so a column filter does not drop it), in the table's sort order.
 */
export function selectedRowsInOrder<T extends { dimension_value: string }>(
  rows: T[],
  selected: ReadonlySet<string>,
  sortRules: SortRule[],
  getValue: (row: T, id: string) => number | string | null | undefined,
): T[] {
  if (selected.size === 0) return [];
  const sorted = sortRules.length ? [...rows].sort(compareByRules(sortRules, getValue)) : rows;
  return sorted.filter((r) => selected.has(r.dimension_value));
}

// ─── column filters as chips and in the URL (owner, 30.10.2026) ───

export const TEXT_OPS: { value: TextOp; label: string }[] = [
  { value: "contains", label: "Contains" },
  { value: "notContains", label: "Does not contain" },
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Does not equal" },
  { value: "startsWith", label: "Starts with" },
  { value: "endsWith", label: "Ends with" },
  { value: "isEmpty", label: "Is empty" },
  { value: "isNotEmpty", label: "Is not empty" },
];

export const NUMBER_OPS: { value: NumberOp; label: string }[] = [
  { value: "eq", label: "=" },
  { value: "ne", label: "≠" },
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
  { value: "between", label: "Between" },
  { value: "inList", label: "In list" },
];

const TEXT_OP_LABEL = new Map(TEXT_OPS.map((o) => [o.value, o.label]));
const NUMBER_OP_LABEL = new Map(NUMBER_OPS.map((o) => [o.value, o.label]));

/** A column filter, phrased for its chip (e.g. `> 1000`, `contains "foo"`). */
export const describeColumnFilter = (f: ColumnFilter): string => {
  if (f.kind === "text") {
    if (f.op === "isEmpty" || f.op === "isNotEmpty") return TEXT_OP_LABEL.get(f.op) ?? f.op;
    return `${TEXT_OP_LABEL.get(f.op) ?? f.op} "${f.value}"`;
  }
  if (f.op === "between") return `${NUMBER_OP_LABEL.get(f.op)} ${f.min}–${f.max}`;
  if (f.op === "inList") return `${NUMBER_OP_LABEL.get(f.op)} ${f.values.join(", ")}`;
  return `${NUMBER_OP_LABEL.get(f.op) ?? f.op} ${f.value}`;
};

/** A filter back into what its editor shows: draftToFilter(filterToDraft(f)) is f. */
export function filterToDraft(f: ColumnFilter): FilterDraft {
  if (f.kind === "text") return { op: f.op, a: f.value, b: "" };
  if (f.op === "between") return { op: f.op, a: String(f.min), b: String(f.max) };
  if (f.op === "inList") return { op: f.op, a: f.values.join(", "), b: "" };
  return { op: f.op, a: String(f.value), b: "" };
}

const finite = z.number().finite();
const columnFilterSchema = z.union([
  z.object({
    kind: z.literal("text"),
    op: z.enum(["contains", "notContains", "equals", "notEquals", "startsWith", "endsWith", "isEmpty", "isNotEmpty"]),
    value: z.string().max(2000),
  }),
  z.object({ kind: z.literal("number"), op: z.enum(["eq", "ne", "gt", "gte", "lt", "lte"]), value: finite }),
  z.object({ kind: z.literal("number"), op: z.literal("between"), min: finite, max: finite }),
  z.object({ kind: z.literal("number"), op: z.literal("inList"), values: z.array(finite).min(1).max(500) }),
]);

/**
 * The URL's `where` param: column filters by column id. Anything broken -
 * bad JSON, an unknown operator, NaN - is dropped entry by entry, never thrown:
 * an old or hand-edited link must still open the page.
 */
export function parseColumnFilters(param: string | null): Record<string, ColumnFilter> {
  if (!param?.trim() || param.length > 200_000) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(param);
  } catch {
    return {};
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, ColumnFilter> = {};
  for (const [id, val] of Object.entries(raw)) {
    const f = columnFilterSchema.safeParse(val);
    if (f.success) out[id] = f.data;
  }
  return out;
}

export const serializeColumnFilters = (filters: Record<string, ColumnFilter>): string | null =>
  Object.keys(filters).length ? JSON.stringify(filters) : null;

/**
 * The first column means whatever the grouping is, so its filter is stored
 * with the grouping it was set under ("dimension:domain") and applies only
 * there; every other column by its id.
 */
const DIMENSION_PREFIX = "dimension:";

/**
 * The stored filters that apply to the grid as it is now: the first column's
 * only under its own grouping, the rest while their column is on screen
 * (keepAfterRegroup) - a link with a hidden or renamed column just loses that
 * filter instead of hiding rows nobody can see a reason for.
 */
export function liveColumnFilters(
  stored: Record<string, ColumnFilter>,
  ctx: { dims: string[]; columns: Set<string> },
): Record<string, ColumnFilter> {
  const sig = ctx.dims.join(",");
  const out: Record<string, ColumnFilter> = {};
  for (const [key, f] of Object.entries(stored)) {
    if (key.startsWith(DIMENSION_PREFIX)) {
      if (key.slice(DIMENSION_PREFIX.length) === sig && f.kind === "text") out.dimension = f;
      continue;
    }
    if (key === "dimension") continue;
    // keepAfterRegroup keeps Brand / Campaign Group whether shown or not (a
    // regroup rule); live, a hidden column's condition is parked like any other.
    if ((key === "brand" || key === "campaign_group") && !ctx.columns.has(key)) continue;
    if (keepAfterRegroup(key, { prevDims: ctx.dims, dims: ctx.dims, columns: ctx.columns })) out[key] = f;
  }
  return out;
}

/**
 * A change of the live filters written back without losing the parked ones -
 * the first column's under another grouping, a hidden column's - which apply
 * again when their grouping or column is back. Overwriting with the live set
 * wiped them on any unrelated edit (ui(2) review, 30.10.2026). A bare
 * "dimension" key (no grouping) is never live and is dropped here.
 */
export function mergeColumnFilters(
  stored: Record<string, ColumnFilter>,
  nextLive: Record<string, ColumnFilter>,
  ctx: { dims: string[]; columns: Set<string> },
): Record<string, ColumnFilter> {
  const parked = Object.fromEntries(parkedColumnFilters(stored, ctx).map((p) => [p.key, p.filter]));
  return { ...parked, ...storeColumnFilters(nextLive, ctx.dims) };
}

/**
 * The stored filters that do not apply now, and why: "grouping" - the first
 * column's under another grouping, or a column this grouping has no values
 * for (GSC off domains); "hidden" - its column is not shown. The bar shows
 * them as inactive chips (owner, 30.10.2026) instead of keeping them out of
 * sight in the link.
 */
export function parkedColumnFilters(
  stored: Record<string, ColumnFilter>,
  ctx: { dims: string[]; columns: Set<string> },
): { key: string; filter: ColumnFilter; reason: "grouping" | "hidden" }[] {
  const liveKeys = new Set(Object.keys(storeColumnFilters(liveColumnFilters(stored, ctx), ctx.dims)));
  return Object.entries(stored)
    .filter(([k]) => k !== "dimension" && !liveKeys.has(k))
    .map(([key, filter]) => ({
      key,
      filter,
      reason: key.startsWith(DIMENSION_PREFIX) || ctx.columns.has(key) ? ("grouping" as const) : ("hidden" as const),
    }));
}

/**
 * Text or number, the way the grid's header filter decides it - also for a
 * column that is hidden or a first column stored for another grouping, so a
 * chip or the builder never edits a text condition as a number (review 30.10).
 */
export const columnKindOf = (key: string): ColKind =>
  TEXT_COLUMNS.has(key) || key.startsWith(DIMENSION_PREFIX) ? "text" : "number";

/** The grid's filters as the URL stores them (see liveColumnFilters). */
export function storeColumnFilters(
  live: Record<string, ColumnFilter>,
  dims: string[],
): Record<string, ColumnFilter> {
  const out: Record<string, ColumnFilter> = {};
  for (const [key, f] of Object.entries(live)) out[key === "dimension" ? DIMENSION_PREFIX + dims.join(",") : key] = f;
  return out;
}
